import axios from 'axios';
import i18n from 'i18next';
import { version } from '../../config/version';
import { getScannerAccessToken, isScannerDevice, resumeScannerSession } from '../scannerSession';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// The API returns English messages plus a stable error_code. Swap in the
// translated message (locales/<lng>/errors.json) so every place that shows
// `response.data.message` — toasts, form banners, dialogs — speaks the user's
// language. Unknown codes keep the server's text.
function localizeError(error) {
  const data = error.response?.data;
  if (!error.response) {
    // No response at all: offline, CORS, server down
    if (!axios.isCancel(error)) error.message = i18n.t('errors:NETWORK');
    return error;
  }
  if (data && typeof data === 'object') {
    const code = data.error_code;
    if (code && i18n.exists(`errors:${code}`)) {
      data.server_message = data.message;
      data.message = i18n.t(`errors:${code}`);
    } else if (!code && error.response.status >= 500) {
      data.message = i18n.t('errors:SERVER');
    }
  } else if (error.response.status >= 500) {
    error.response.data = { message: i18n.t('errors:SERVER') };
  }
  return error;
}

// Callback for handling forbidden errors with reference IDs
let forbiddenErrorCallback = null;

export function setForbiddenErrorCallback(callback) {
  forbiddenErrorCallback = callback;
}

function isForbiddenWithReferenceId(error) {
  if (error.response?.status !== 403) {
    return false;
  }

  const data = error.response?.data;
  return (
    data &&
    data.error_code === 'FORBIDDEN' &&
    typeof data.reference_id === 'string'
  );
}

class ApiClient {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'x-version': version,
      },
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        // A staff login wins; otherwise a scanner phone uses its own token
        const token = localStorage.getItem('access_token') || getScannerAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Track pageid
        if (window.pageInstanceId) {
          config.headers['X-Page-Instance-Id'] = window.pageInstanceId;
        }

        // Add current route
        config.headers['X-Current-Route'] = window.location.pathname;
          return config;
        },

      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh and forbidden errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        localizeError(error);
        const originalRequest = error.config;

        // Handle 403 Forbidden with reference ID
        if (isForbiddenWithReferenceId(error)) {
          const referenceId = error.response.data.reference_id;
          if (forbiddenErrorCallback) {
            forbiddenErrorCallback(referenceId);
          }
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Scanner phone without a staff login: trade the device token for a new access token
          if (!localStorage.getItem('refresh_token') && isScannerDevice()) {
            try {
              const session = await resumeScannerSession();
              if (session.access_token) {
                originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
                return this.client(originalRequest);
              }
            } catch (resumeError) {
              if (resumeError.expired) window.location.href = '/login';
            }
            return Promise.reject(error);
          }

          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              const response = await this.refreshToken(refreshToken);
              const { token: newAccessToken, refresh_token: newRefreshToken } = response.data;

              localStorage.setItem('access_token', newAccessToken);
              localStorage.setItem('refresh_token', newRefreshToken);

              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            this.clearTokens();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  async refreshToken(refreshToken) {
    return axios.post(`${API_BASE_URL}/api/v1/ac/auth/refresh`, {
      token: localStorage.getItem('access_token'),
      refresh_token: refreshToken,
    });
  }

  clearTokens() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  async get(url, config) {
    return this.client.get(url, config);
  }

  async post(url, data, config) {
    return this.client.post(url, data, config);
  }

  async put(url, data, config) {
    return this.client.put(url, data, config);
  }

  async delete(url, config) {
    return this.client.delete(url, config);
  }

  async patch(url, data, config) {
    return this.client.patch(url, data, config);
  }
}

export const apiClient = new ApiClient();